import React from 'react';

import { push } from 'connected-react-router';

import styled from 'styled-components';

import moment from 'moment';

import { AnimatePresence } from 'framer-motion';

import { Translate } from 'react-localize-redux';

// Types
import SimpleArtist from '@common/types/artist/simple-artist';
import CatalogRelease from '@common/types/catalog/catalog-release';

import { getMenuOptions } from '../../menu-options';

// Components
import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Pill from '@components/basics/pill';
import StatusLabel from '@components/status-label';
import DotsIcon from '@components/basics/icons/dots';
import SquareImg from '@components/basics/artwork-img';
import Typography from '@components/basics/typography';
import FixedBottomMenu from '@components/layout/fixed-bottom-menu';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';
import CircleLockIcon from '@components/basics/icons/circle-lock';
import { TakedownPending } from '@components/catalog-row/desktop-view/info-row';
import { calculateTimeLeft } from '@components/catalog-row/calculate-time-left';
import CircleText from '@components/basics/circle-text';
import SmallNotification from '@components/small-notification';
import { isCatalogEntryLocked } from '../../../../store/catalog/selectors';
import { OpenMenu } from '../../../../store/ui/action-creators';

interface MobileInfoRowProps {
  className?: string;
  user: any;
  item: CatalogRelease;
  release: CatalogRelease | undefined;
  isTrackListOpen: boolean;
  artist: SimpleArtist;
  isReleases: boolean;
  isFilteredByAddedDate: boolean;
  openTrackList: () => void;
  handleCatalogMenu: (
    action: string,
    release: CatalogRelease,
    artist: SimpleArtist,
    user: any
  ) => void;
  showNotificationMenu: () => void;
  openDealForRelease: () => OpenMenu;
}

export default function MobileInfoRow({
  className,
  user,
  item,
  artist,
  isReleases,
  isTrackListOpen,
  isFilteredByAddedDate,
  openTrackList,
  handleCatalogMenu,
  showNotificationMenu,
  openDealForRelease,
}: MobileInfoRowProps) {
  const menuOptions = getMenuOptions(item, artist, user);

  const [showNotification, setShowNotification] = React.useState(false);
  const [dropdown, setDropdown] = React.useState<boolean>(false);
  const [timeLeft, setTimeLeft]: any = React.useState(calculateTimeLeft(item));

  React.useEffect(() => {
    if (showNotification) {
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
    }
  }, [showNotification]);

  const handleStatusClick = () => {
    if (item.status === 'changes-needed' || item.status === 'rejected') {
      showNotificationMenu();
    }
    if (item.status === 'taken-down')
      push(`/release/${item.artist.id}/${item.id}/title`);
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const diff = calculateTimeLeft(item);
      let width =
        diff.days * 24 * 60 * 60 +
        diff.hours * 60 * 60 +
        diff.minutes * 60 +
        diff.seconds;
      width = width * (100 / (30 * 60));
      setTimeLeft({ ...calculateTimeLeft(item), width: width });
    }, 1000);

    return () => clearTimeout(timer);
  });

  const inMinutes =
    timeLeft.days * 24 * 60 + timeLeft.hours * 60 + timeLeft.minutes;

  const isPublished = moment().isSameOrAfter(moment(item.release_date));

  const getStatus = () => {
    if (item.status === 'published') {
      if (isPublished) {
        if (item.approved || item.approved === undefined) {
          return 'released';
        }
      } else {
        if (item.approved || item.approved === undefined) {
          return 'approved';
        }
      }
    }
    return item.status;
  };

  return (
    <Row
      className={`${className || ''} ${isTrackListOpen ? 'menu-opened' : ''}`}
    >
      <SongContainer
        justifyContent="space-between"
        alignItems="center"
        gap={10}
      >
        <SongInfo alignItems="center" gap={20}>
          <SquareImg size={36} srcImg={item.cover_url} altImg={item.title} />
          <Grid gap={3}>
            <Typography className="song-name" customFont="semi-bold">
              {item.title}
            </Typography>
            <Typography className="song-upc" type="small" weight="700">
              {item.upc || '-'}
            </Typography>
          </Grid>
        </SongInfo>
        <Flex justifyContent="flex-end" alignItems="center" gap={30}>
          {/* TODO: DEVTEAM-603 - Implement NFT for each track. */}
          {item.isNFT && <Pill>NFT</Pill>}

          {item.featuring && (
            <CircleText className={className || ''}>Co</CircleText>
          )}
        </Flex>
      </SongContainer>

      {isFilteredByAddedDate ? (
        <Flex alignItems="center" justifyContent="space-between">
          <Typography weight="700">
            <Translate id="CATALOG.TITLES.ADDED" />
          </Typography>
          <Typography align="right">
            {moment(item.created_at).format('DD MMM YYYY')}
          </Typography>
        </Flex>
      ) : (
        <Flex alignItems="center" justifyContent="space-between">
          <Typography weight="700">
            <Translate id="CATALOG.TITLES.RELEASE" />
          </Typography>
          <Typography align="right">
            {moment(item.release_date).format('DD MMM YYYY')}
          </Typography>
        </Flex>
      )}

      <ActionsContent isReleases={isReleases}>
        {isReleases ? (
          <>
            {item.status !== 'takedown-pending' ? (
              <StatusLabel
                handleStatusClick={handleStatusClick}
                status={getStatus()}
                approved={item.approved}
                releaseDate={item.release_date}
              />
            ) : (
              <TakedownPending progressWidth={100 - timeLeft?.width ?? 0}>
                <Typography
                  type="small"
                  weight="700"
                  align="center"
                  className="takedown-text"
                >
                  <Translate
                    id="CATALOG.STATUS.TAKEDOWN_IN"
                    data={{
                      time: `${inMinutes >= 10 ? inMinutes : '0' + inMinutes}:${
                        timeLeft.seconds >= 10
                          ? timeLeft.seconds
                          : '0' + timeLeft.seconds
                      }`,
                    }}
                  />
                </Typography>
              </TakedownPending>
            )}
          </>
        ) : null}

        <Flex justifyContent="flex-end" alignItems="center" gap={10}>
          {item.tracks.length ? (
            <StyledCircleArrowIcon
              isWhite
              smallArrow
              arrowUp={isTrackListOpen}
              onClick={openTrackList}
            />
          ) : null}
          {menuOptions.length ? (
            <>
              <StyledDotsIcon onClick={() => setDropdown(true)} />
              <AnimatePresence exitBeforeEnter>
                {dropdown && (
                  <FixedBottomMenu
                    title={item.title}
                    isShowing={dropdown}
                    options={menuOptions}
                    onSelect={(action: string) =>
                      handleCatalogMenu(action, item, artist, user)
                    }
                    onClose={() => setDropdown(false)}
                  />
                )}
              </AnimatePresence>
            </>
          ) : null}
          {isCatalogEntryLocked(item) && (
            <CircleLockIcon onClick={() => openDealForRelease()} />
          )}
        </Flex>
      </ActionsContent>
      {showNotification ? (
        <SmallNotification
          notificationType="success"
          title={<Translate id="CATALOG.SNACKBAR.UPC_COPIED" />}
        />
      ) : null}
    </Row>
  );
}

const Row = styled.div`
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  width: 100%;
  display: grid;
  grid-gap: 20px;
  padding: 20px;

  &.menu-opened {
    border-radius: 10px 10px 0 0;
  }
`;

const SongContainer = styled(Flex)`
  position: relative;
`;

const SongInfo = styled(Flex)`
  .song-name {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .song-upc {
    color: ${({ theme }) => theme.colors.tertiary};
  }
`;

const ActionsContent = styled.div<{ isReleases: boolean }>`
  display: flex;
  width: 100%;
  justify-content: ${({ isReleases }) =>
    isReleases ? 'space-between' : 'flex-end'};
`;

const StyledCircleArrowIcon = styled(CircleArrowIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;
const StyledDotsIcon = styled(DotsIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;
